
import React, { useState, useEffect, useCallback } from 'react';
import { useColor, useTheme, useBrand } from '../../context/ColorContext';
import { Droplets, TreePine, Sofa, ArrowRight } from 'lucide-react';
import ProductStack from './ProductStack/ProductStack';

const HeroContent = ({ openLightbox, onOpenVisualizer }) => {
    const { activeColor, brandData } = useColor();
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const [activeIndex, setActiveIndex] = useState(0);
    // Use featured products for homepage; falls back to all products if none featured
    const PRODUCTS = brandData.FEATURED_PRODUCTS || brandData.PRODUCTS;

    const brandTitles = {
        'dufa': { top: 'Renklerİn', bottom: 'Gücü' },
        'genc': { top: 'MobİlyanIn', bottom: 'Ruhu' },
        'woodsol': { top: 'AhşabIn', bottom: 'Kalbi' }
    };

    // Auto-rotation logic (Random) - Stable interval that doesn't reset on every render
    const activeIndexRef = React.useRef(activeIndex);
    activeIndexRef.current = activeIndex;

    const productsLengthRef = React.useRef(PRODUCTS.length);
    productsLengthRef.current = PRODUCTS.length;

    useEffect(() => {
        if (PRODUCTS.length <= 1) return;

        const interval = setInterval(() => {
            let nextIndex;
            do {
                nextIndex = Math.floor(Math.random() * productsLengthRef.current);
            } while (nextIndex === activeIndexRef.current && productsLengthRef.current > 1);

            setActiveIndex(nextIndex);
        }, 4000);

        return () => clearInterval(interval);
    }, [PRODUCTS.length, setActiveIndex]); // Only restart on products count change


    const brandIcon = () => {
        if (activeBrand.id === 'dufa') return <Droplets size={16} />;
        if (activeBrand.id === 'genc') return <Sofa size={16} />;
        return <TreePine size={16} />;
    };

    const title = brandTitles[activeBrand.id] || brandTitles['dufa'];
    const activeProduct = PRODUCTS[activeIndex] || { name: activeBrand.name, desc: activeBrand.description };

    return (
        <div className="flex flex-col lg:flex-row items-center justify-around lg:justify-between h-full lg:h-auto min-h-[500px] lg:min-h-0 py-0 relative lg:mt-10 pointer-events-none">
            {/* Text Area */}
            <div className="text-center lg:text-left lg:max-w-xl animate-in fade-in slide-in-from-left-10 duration-700 relative flex flex-col items-center lg:items-start shrink-0 pointer-events-auto">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${theme === 'dark' ? 'border-white/20 bg-white/5 text-white/80' : 'border-black/10 bg-black/5 text-slate-600'} backdrop-blur-md mb-4 lg:mb-6`}>
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: activeColor?.code || activeBrand.primaryColor }}></div>
                    <span className="text-[10px] md:text-xs font-bold uppercase tracking-[0.2em]">{activeBrand.id === 'dufa' ? 'Yeni Sezon Renkleri' : activeBrand.id === 'genc' ? 'Trend Mobilya Renkleri' : 'Doğal Ahşap Dokuları'}</span>
                </div>
                <h1 className={`text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black leading-tight tracking-tighter uppercase mb-2 lg:mb-4 drop-shadow-2xl ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                    {title.top} <br />
                    <span className={`text-transparent bg-clip-text ${theme === 'dark' ? 'bg-gradient-to-r from-white to-white/50' : 'bg-gradient-to-r from-slate-800 to-slate-500'}`}>{title.bottom}</span>
                </h1>

                <div className="relative min-h-[90px] sm:min-h-[70px] lg:min-h-[110px] mb-4 lg:mb-6 w-full lg:w-auto">
                    <p
                        key={activeIndex}
                        className={`text-[11px] sm:text-base md:text-lg font-light leading-relaxed max-w-[280px] md:max-w-sm lg:max-w-md mx-auto lg:mx-0 px-2 lg:px-0 animate-in fade-in slide-in-from-top-4 duration-1000 ${theme === 'dark' ? 'text-white/70' : 'text-slate-700'}`}
                    >
                        {activeProduct.desc}
                    </p>
                </div>

                <div className="flex justify-center lg:justify-start relative z-10 w-full sm:w-auto">
                    <button
                        onClick={onOpenVisualizer}
                        className="group relative px-8 sm:px-10 py-4 text-white text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] rounded-xl transition-all duration-300 hover:scale-[1.03] flex items-center justify-center gap-3 overflow-hidden"
                        style={{
                            backgroundColor: activeBrand.primaryColor,
                            boxShadow: `0 18px 40px -10px ${activeBrand.primaryColor}80, 0 0 0 1px rgba(255,255,255,0.12) inset`
                        }}
                    >
                        {/* Glossy top highlight */}
                        <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/10" />

                        {/* Shimmer sweep on hover */}
                        <span className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 skew-x-12 bg-gradient-to-r from-transparent via-white/40 to-transparent transition-all duration-700 ease-out group-hover:left-[110%]" />

                        <Droplets size={16} className="relative z-10 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />
                        <span className="relative z-10 whitespace-nowrap">Odanı Canlandır</span>
                        <ArrowRight size={14} className="relative z-10 -ml-1 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                </div>
            </div>

            {/* Product Stack (Right side on Desktop, Middle on Mobile) */}
            <div className="w-full lg:w-auto flex justify-center perspective-1000 lg:mr-10 items-center py-10 lg:py-0 pointer-events-auto z-10">
                <ProductStack
                    openLightbox={openLightbox}
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                />
            </div>
        </div>
    );
};

export default HeroContent;
